import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchEnrolledCoursesByTeacherId } from "../../../redux/Actions/manageStudentsActions";
import TeacherNavbar from "./TeacherNavbar";
import "./ManageStudents.css";

const ManageStudents = () => {
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const roles = [...(authUser?.roleNames || []), authUser?.roleName].filter(Boolean).map((role) => String(role).toUpperCase());
  const isTeacherManager = roles.includes("TEACHER_MANAGER");

  const { loading, enrolledCourses, error } = useSelector(
    (state) => state.manageStudents
  );
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [alphabeticalOrder, setAlphabeticalOrder] = useState("none"); // Initial 'none' option
  const [dateOrder, setDateOrder] = useState("none"); // Initial 'none' option

  const getCourseLevel = (course) => {
    const title = course?.courseTitle || "";
    return (
      course?.parentTitle ||
      title.match(/^[A-C][0-2]/i)?.[0]?.toUpperCase() ||
      course?.courseLevel ||
      title ||
      "N/A"
    );
  };

  useEffect(() => {
    dispatch(fetchEnrolledCoursesByTeacherId());
  }, [dispatch]);

  const toggleExpand = (courseId) => {
    setExpandedCourseId(expandedCourseId === courseId ? null : courseId);
  };

  const sortedCourses = useMemo(() => {
    let sorted = [...enrolledCourses];

    // Apply alphabetical sorting if selected
    if (alphabeticalOrder !== "none") {
      sorted = sorted.sort((a, b) =>
        alphabeticalOrder === "asc"
          ? getCourseLevel(a).localeCompare(getCourseLevel(b))
          : getCourseLevel(b).localeCompare(getCourseLevel(a))
      );
    }

    // Apply start date sorting if selected
    if (dateOrder !== "none") {
      sorted = sorted.sort((a, b) =>
        dateOrder === "asc"
          ? new Date(a.startDate) - new Date(b.startDate)
          : new Date(b.startDate) - new Date(a.startDate)
      );
    }

    return sorted;
  }, [enrolledCourses, alphabeticalOrder, dateOrder]);

  return (
    <div className="manage-students-layout">
      <TeacherNavbar />
      <Container className="manage-students-container">
        <Box className="manage-students-content">
          <Typography
            variant="h4"
            gutterBottom
            className="manage-students-title"
          >
            Manage Students
          </Typography>

          <Divider style={{ margin: "20px 0" }} />

          {/* Sort Filters */}
          <Box display="flex" gap={3} mb={2}>
            <FormControl variant="outlined" size="small">
              <InputLabel>Alphabetical Order</InputLabel>
              <Select
                value={alphabeticalOrder}
                onChange={(e) => setAlphabeticalOrder(e.target.value)}
                label="Alphabetical Order"
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: "200px" }}
            >
              <InputLabel>Start Date Order</InputLabel>
              <Select
                value={dateOrder}
                onChange={(e) => setDateOrder(e.target.value)}
                label="Start Date Order"
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="asc">Oldest First</MenuItem>
                <MenuItem value="desc">Newest First</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Divider style={{ margin: "20px 0" }} />

          {loading && <Typography>Loading enrolled courses...</Typography>}
          {error && (
            <Typography color="error" style={{ marginLeft: "20px" }}>
              Error: {error}
            </Typography>
          )}
          {!loading && sortedCourses.length === 0 && (
            <Typography>No enrolled courses available.</Typography>
          )}

          <div className="manage-students-course-cards-container">
            {sortedCourses.map((course) => (
              <Card
                key={course.teacherCourseId}
                className="student-course-card"
              >
                <CardContent>
                  <Typography variant="h6" className="course-title">
                    {course.courseTitle} <Typography component="span" color="text.secondary">({getCourseLevel(course)})</Typography>
                  </Typography>
                  {isTeacherManager && <Typography variant="body2"><strong>Teacher:</strong> {course.teacherFirstName} {course.teacherLastName} · {course.teacherEmail}</Typography>}
                  <Divider />
                  <Typography variant="body2" className="course-dates">
                    Start Date: {course.startDate} <br />
                    End Date: {course.endDate}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => toggleExpand(course.teacherCourseId)}
                    style={{ marginTop: "10px" }}
                  >
                    {expandedCourseId === course.teacherCourseId
                      ? "Hide Details"
                      : "Show Details"}
                  </Button>
                </CardContent>
                <Collapse in={expandedCourseId === course.teacherCourseId}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Level Details
                    </Typography>
                    <Typography variant="body2">
                      {course.courseDescription || "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}><strong>Schedule:</strong> {(course.daysOfWeek || []).join(", ") || "-"} · {course.startTime || "-"}–{course.endTime || "-"}</Typography>

                    <Typography
                      variant="h6"
                      gutterBottom
                      style={{ marginTop: "10px" }}
                    >
                      Enrolled Students
                    </Typography>
                    <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Table sx={{ minWidth: isTeacherManager ? 900 : 520 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>First Name</TableCell>
                          <TableCell>Last Name</TableCell>
                          <TableCell>Email</TableCell>
                          {isTeacherManager && <TableCell>Phone</TableCell>}
                          {isTeacherManager && <TableCell>Accommodation</TableCell>}
                          {isTeacherManager && <TableCell>Payment Method</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {course.enrolledStudents.map((student) => (
                          <TableRow key={student.studentId}>
                            <TableCell>{student.firstName}</TableCell>
                            <TableCell>{student.lastName}</TableCell>
                            <TableCell>{student.email}</TableCell>
                            {isTeacherManager && <TableCell>{student.phoneNumber || "-"}</TableCell>}
                            {isTeacherManager && <TableCell>{student.accommodation || "-"}</TableCell>}
                            {isTeacherManager && <TableCell>{student.paymentMethod || "-"}</TableCell>}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </Box>
                  </CardContent>
                </Collapse>
              </Card>
            ))}
          </div>
        </Box>
      </Container>
    </div>
  );
};

export default ManageStudents;
